import { CommonModule } from '@angular/common';
import {
  isNullOrEmpty,
  LocalizationService,
  SubscriptionService,
  LocalizationModule,
} from '@abp/ng.core';
import { ConfirmationService } from '@abp/ng.theme.shared';
import { ColDef, FilterModel } from '@ag-grid-community/core';
import { Component, computed, inject, Injector, OnInit, Signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  DwGridDataSourceService,
  FieldTypes,
  IEnumerationCellRendererParams,
} from '@digiwall/ag-grid';
import {
  CustomerSalesPurchaseListDto,
  CustomerSalesPurchaseService,
} from '@digiwall/goliath-documents/proxy';
import { OdysseyOffcanvas } from '@digiwall/core';
import { SalesPurchaseImportComponent } from './import/customer-sales-purchase-import.component';
import { BaseCustomerDocList } from '../../base-customer-docs/base-customer-doc-list';
import { ListPageComponent } from '@digiwall/ag-grid';
import { AgGridComponent } from '@digiwall/ag-grid';
import { DropdownAction } from '@digiwall/ui-kit';
import { SalesPurchaseDialogBoxCloneComponent } from '../customer-sales-purchase-detail/dialog-box-clone/dialog-box-clone.component';

@Component({
  selector: 'document-customer-sales-purchase-list',
  templateUrl: './customer-sales-purchase-list.component.html',
  styles: `
    :host {
      flex-grow: 1;
    }
  `,
  providers: [DwGridDataSourceService, SubscriptionService],
  imports: [CommonModule, ListPageComponent, AgGridComponent, LocalizationModule],
})
export class CustomerSalesPurchaseListComponent
  extends BaseCustomerDocList<CustomerSalesPurchaseListDto, CustomerSalesPurchaseService>
  implements OnInit
{
  private canvaService = inject(OdysseyOffcanvas);
  predefinedFilters: FilterModel;
  private _columns: ColDef[] = [
    ...this.colDefs,
    {
      field: 'requestedDeliveryDate',
      headerName: this.localizationService.instant(
        'Doc::CustomerSalesPurchase:PreferredDeliveryDate',
      ),
      type: FieldTypes.Date,
      hide: true,
    },
    {
      field: 'interventionDate',
      headerName: this.localizationService.instant('Doc::CustomerSalesPurchase:InterventionDate'),
      type: FieldTypes.Date,
      hide: true,
    },
    {
      field: 'purchaseOrderStatusId',
      headerName: this.localizationService.instant('Doc::PurchaseOrderStatusId'),
      type: FieldTypes.Enumeration,
      cellRendererParams: {
        entities: this.documentEnumerationTypeStore.purchaseOrderStatus,
      } as IEnumerationCellRendererParams,
    },
    {
      field: 'depositAmount',
      colId: 'DepositAmount_RoundedPrice',
      headerName: this.localizationService.instant('Doc::DepositAmount'),
      type: FieldTypes.Currency,
      hide: true,
    },
    {
      field: 'balanceDue',
      colId: 'BalanceDue_RoundedPrice',
      headerName: this.localizationService.instant('Doc::BalanceDue'),
      type: FieldTypes.Currency,
      hide: true,
    },
    {
      field: 'notifiedConfirmedCount',
      headerName: this.localizationService.instant('Doc::NotifiedConfirmedCount'),
      type: FieldTypes.Number,
      hide: true,
    },
    {
      field: 'lastConfirmationNotificationDate',
      headerName: this.localizationService.instant('Doc::LastConfirmationNotificationDate'),
      type: FieldTypes.DateTime,
      hide: true,
    },
    {
      field: 'notifiedReadyCount',
      headerName: this.localizationService.instant('Doc::NotifiedReadyCount'),
      type: FieldTypes.Number,
      hide: true,
    },
    {
      field: 'lastReadyNotificationDate',
      headerName: this.localizationService.instant('Doc::LastReadyNotificationDate'),
      type: FieldTypes.DateTime,
      hide: true,
    },
  ];

  get columns(): ColDef[] {
    return this._columns;
  }

  protected toolbarActions: Signal<DropdownAction[]> = computed(() => {
    const actions = this.importAction() ? [this.importAction()] : [];
    return actions.concat([
      {
        label: '::Import',
        icon: 'digi-out-upload',
        action: () => {
          this.canvaService
            .open(SalesPurchaseImportComponent, null, {
              panelClass: 'w-100 w-sm-25',
            })
            .closed.subscribe(salesPurchase => {
              if (salesPurchase != null) {
                this.router.navigate([salesPurchase.id], { relativeTo: this.route });
              }
            });
        },
      },
    ]);
  });

  constructor(
    protected salesPurchaseService: CustomerSalesPurchaseService,
    router: Router,
    route: ActivatedRoute,
    localizationService: LocalizationService,
    subscriptionService: SubscriptionService,
    confirmation: ConfirmationService,
    datasource: DwGridDataSourceService,
    injector: Injector,
  ) {
    super(
      salesPurchaseService,
      router,
      route,
      localizationService,
      subscriptionService,
      confirmation,
      datasource,
      injector,
    );

    // Default view: minimal columns.
    this.applyDefaultColumnVisibility();
    this._columns = this.orderColumns(this._columns, [
      'displayNumber',
      'documentDate',
      'isClosed',
      'thirdPartyCode',
      'thirdPartyName',
      'reference',
      'totalAmount',
      'purchaseOrderStatusId',
    ]);
    this.applyColumnWidths(
      {
        displayNumber: 110,
        documentDate: 150,
        isClosed: 110,
        thirdPartyCode: 150,
        thirdPartyName: 200,
        totalAmount: 110,
        purchaseOrderStatusId: 150,
      },
      'reference',
      180,
    );
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(queryParams => {
      if (!isNullOrEmpty(queryParams['thirdParty'])) {
        this.predefinedFilters = {
          thirdPartyCode: { filterType: 'text', type: 'equals', filter: queryParams['thirdParty'] },
          isClosed: { filterType: 'set', values: ['false'] },
        };
      } else if (!isNullOrEmpty(queryParams['thirdPartyCode'])) {
        this.predefinedFilters = {
          thirdPartyCode: {
            filterType: 'text',
            type: 'equals',
            filter: queryParams['thirdPartyCode'],
          },
          isClosed: { filterType: 'set', values: ['false'] },
        };
      }
    });

    super.ngOnInit();
  }

  duplicateSalesPurchase() {
    this.salesPurchaseService.getAnnexesCount(this.selectedEntities()[0].id).subscribe(count => {
      this.duplicateDoc(count != 0);
    });
  }

  protected override cloneComponent = SalesPurchaseDialogBoxCloneComponent;
}
